import dayjs from "dayjs";
import styled from "styled-components";
import { useState, useCallback, useEffect } from "react";
import { Upload, Progress, Image, Empty } from "antd";
import { useIntl } from "react-intl";
import { CiInboxIn } from "react-icons/ci";
import { RcFile } from "antd/es/upload";
import { toast } from "react-toastify";
import {
  TransactionAttachmentFile,
  TransactionAttachmentStatus,
} from "@lib/types";
import { FaRegFileAlt } from "react-icons/fa";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";
import FilePreviewModal from "./FilePreviewModal";
import { apiOpenTransactionFile } from "@lib/api/transaction";
import { ManagerName } from "../Others";

const { Dragger } = Upload;

const UploadContainer = styled.div`
  .ant-upload-drag {
    border: 2px dashed #d9d9d9;
    border-radius: 8px;
    background: #fafafa;
    transition: all 0.3s ease;

    &:hover {
      border-color: #1890ff;
      background: #f0f8ff;
    }

    &.ant-upload-drag-hover {
      border-color: #1890ff;
      background: #f0f8ff;
    }
  }

  .upload-preview {
    margin-top: 16px;
    padding: 16px;
    border: 1px solid #d9d9d9;
    border-radius: 8px;
    background: #fafafa;
  }

  .file-item {
    margin-bottom: 12px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .preview-content {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .file-info {
    flex: 1;

    .file-name {
      font-weight: 500;
      margin-bottom: 4px;
    }

    .file-size {
      color: #666;
      font-size: 12px;
    }
  }

  .preview-actions {
    display: flex;
    gap: 8px;
  }

  .progress-container {
    margin-top: 12px;
    padding: 8px 0;
  }
`;

export type FileUploadProps = {
  uploadedFiles: TransactionAttachmentFile[];
  onUpload: (
    file: RcFile,
    onProgress: (progress: number) => void,
  ) => Promise<void>;
  onRemove?: (file: TransactionAttachmentFile) => void;
  onFilesChange?: (files: TransactionAttachmentFile[]) => void;
  disabled?: boolean;
  acceptedTypes?: string[];
  maxSize?: number; // in MB
  canDelete?: boolean;
  canUpload?: boolean;
  showManager?: boolean;
  showManagerLink?: boolean;
};

export default function TransactionFileUpload({
  uploadedFiles,
  onUpload,
  onRemove,
  onFilesChange,
  disabled = false,
  acceptedTypes = ["image/*", ".pdf", ".doc", ".docx"],
  maxSize = 10, // 10MB default
  canDelete = true,
  canUpload = true,
  showManager = true,
  showManagerLink = true,
}: FileUploadProps) {
  // console.log("🚀 ~ FileUpload ~ uploadedFiles:", uploadedFiles);
  const { messages } = useIntl();
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [previewFile, setPreviewFile] =
    useState<TransactionAttachmentFile | null>(null);
  const [localFiles, setLocalFiles] = useState<TransactionAttachmentFile[]>([]);
  const [showAllFiles, setShowAllFiles] = useState(false);

  // Sync local files with uploaded files
  useEffect(() => {
    setLocalFiles(uploadedFiles);
  }, [uploadedFiles]);

  const formatFileSize = (bytes: number) => {
    if (bytes === null || bytes === undefined) return "Unknown Size";
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const isImageFile = (file: File) => {
    return file?.type?.startsWith("image/");
  };

  const createPreviewUrl = (file: File) => {
    if (isImageFile(file)) {
      return URL.createObjectURL(file);
    }
    return null;
  };

  const handleFileSelect = useCallback(
    async (file: RcFile) => {
      // Validate file size
      if (file.size > maxSize * 1024 * 1024) {
        toast.error(
          `${messages["common.fileSizeExceeded"] as string} ${maxSize}MB`,
        );
        return false;
      }

      // Validate file type
      const isValidType = acceptedTypes.some((type) => {
        if (type.startsWith(".")) {
          return file.name.toLowerCase().endsWith(type.toLowerCase());
        }
        return file.type.match(type.replace("*", ".*"));
      });

      if (!isValidType) {
        toast.error(messages["common.invalidFileType"] as string);
        return false;
      }

      const fileId = Date.now().toString() + Math.random().toString(36);
      const newFile: TransactionAttachmentFile = {
        ...file,
        uid: fileId,
        previewUrl: createPreviewUrl(file),
        progress: 0,
        status: TransactionAttachmentStatus.Uploading,
        originalFileName: file.name,
        size: file.size,
        type: file.type,
        name: file.name,
      };

      // Add new file to the list
      const updatedFiles = [...localFiles, newFile];
      setLocalFiles(updatedFiles);
      onFilesChange?.(updatedFiles);

      try {
        await onUpload(file, (progress) => {
          console.log(`Upload progress for ${file.name}: ${progress}%`);
          // Update local state and notify parent
          setLocalFiles((prevFiles) => {
            const updated = prevFiles.map((f) =>
              f.uid === fileId
                ? {
                    ...f,
                    progress,
                    status: TransactionAttachmentStatus.Uploading,
                  }
                : f,
            );
            onFilesChange?.(updated);
            return updated;
          });
        });

        // Mark as completed
        setLocalFiles((prevFiles) => {
          const updated = prevFiles.map((f) =>
            f.uid === fileId
              ? {
                  ...f,
                  status: TransactionAttachmentStatus.Done,
                  progress: 100,
                }
              : f,
          );
          onFilesChange?.(updated);
          return updated;
        });
        toast.success(messages["common.fileUploadedSuccessfully"] as string);
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : (messages["common.fileUploadFailed"] as string);
        // Mark as error
        setLocalFiles((prevFiles) => {
          const updated = prevFiles.map((f) =>
            f.uid === fileId
              ? {
                  ...f,
                  status: TransactionAttachmentStatus.Error,
                  error: errorMessage,
                }
              : f,
          );
          onFilesChange?.(updated);
          return updated;
        });
        toast.error(errorMessage);
        console.error("Upload error:", error);
      }

      return false; // Prevent default upload behavior
    },
    [onUpload, maxSize, acceptedTypes, messages, localFiles, onFilesChange],
  );

  const handleRemove = async (file: TransactionAttachmentFile) => {
    if (!canDelete) return;
    const fileId = file.uid;
    const fileToRemove = localFiles.find((f) => f.uid === fileId);
    if (fileToRemove?.previewUrl) {
      URL.revokeObjectURL(fileToRemove.previewUrl);
    }
    const filteredFiles = localFiles.filter((f) => f.uid !== fileId);
    setLocalFiles(filteredFiles);
    onFilesChange?.(filteredFiles);
    onRemove?.(file);
  };

  const handleDownload = async (file: TransactionAttachmentFile) => {
    try {
      let blob: Blob;
      const fileName = file.originalFileName || file.name || "download";

      if (file.data) {
        blob = await apiOpenTransactionFile(file.data);
      } else if (file instanceof File) {
        blob = file;
      } else {
        const previewUrl = (file as TransactionAttachmentFile).previewUrl;
        if (previewUrl) {
          const response = await fetch(previewUrl);
          blob = await response.blob();
        } else {
          toast.error(messages["common.fileNotFound"] as string);
          return;
        }
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : (messages["common.fileUploadFailed"] as string);
      toast.error(errorMessage);
      console.error("Download error:", error);
    }
  };

  const handlePreview = (file: TransactionAttachmentFile) => {
    setPreviewFile(file);
    setPreviewModalVisible(true);
  };

  const handleClosePreview = () => {
    setPreviewModalVisible(false);
    setPreviewFile(null);
  };

  // Sort files by upload time (newest first)
  const sortedFiles = [...localFiles].sort((a, b) => {
    // If files have timestamps, use them
    if (a.lastModified && b.lastModified) {
      return (
        new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime()
      );
    }
    // Otherwise, use uid as fallback (newer files have higher uid values)
    const aUid = parseInt(a.uid) || 0;
    const bUid = parseInt(b.uid) || 0;
    return bUid - aUid;
  });

  // Get files to display based on showAllFiles state
  const displayFiles = showAllFiles ? sortedFiles : sortedFiles.slice(0, 4);
  const hasMoreFiles = sortedFiles.length > 4;

  const uploadProps = {
    name: "file",
    multiple: true,
    beforeUpload: handleFileSelect,
    showUploadList: false,
    disabled: disabled,
  };

  return (
    <UploadContainer>
      {canUpload && (
        <Dragger {...uploadProps}>
          <p className="ant-upload-drag-icon">
            <CiInboxIn style={{ fontSize: 48, color: "#1890ff" }} />
          </p>
          <p className="ant-upload-text">
            {messages["common.clickOrDragFileToUpload"] as string}
          </p>
          <p className="ant-upload-hint">
            {messages["common.supportedFormats"] as string}:{" "}
            {acceptedTypes.join(", ")}
            <br />
            {messages["common.maxFileSize"] as string}: {maxSize}MB
          </p>
        </Dragger>
      )}

      <div style={{ marginTop: 16 }}>
        {localFiles.length > 0 ? (
          <div className="upload-preview">
            {displayFiles.map((uploadedFile) => (
              <div key={uploadedFile.uid} className="file-item">
                <div className="preview-content">
                  {isImageFile(uploadedFile) && uploadedFile?.previewUrl ? (
                    <Image
                      src={uploadedFile.previewUrl}
                      alt="Preview"
                      style={{ width: 60, height: 60, objectFit: "cover" }}
                      preview={false}
                    />
                  ) : (
                    <div className="file-icon">
                      <FaRegFileAlt style={{ fontSize: 24 }} />
                    </div>
                  )}

                  <div className="file-info">
                    <div className="file-name">
                      {uploadedFile?.originalFileName || uploadedFile?.name}
                    </div>

                    <div className="file-size">
                      {formatFileSize(uploadedFile.size)}
                    </div>

                    {uploadedFile.lastModified && (
                      <div
                        className="file-date"
                        style={{
                          color: "#666",
                          fontSize: "11px",
                          marginTop: "2px",
                          marginRight: 10,
                        }}
                      >
                        {dayjs(uploadedFile.lastModified).format(
                          "DD.MM.YYYY HH:mm",
                        )}
                      </div>
                    )}

                    {uploadedFile.manager && showManager && (
                      <ManagerName
                        manager={uploadedFile.manager}
                        link={showManagerLink}
                      />
                    )}

                    {uploadedFile.status === "error" && uploadedFile.error && (
                      <div
                        className="file-error"
                        style={{
                          color: "#ff4d4f",
                          fontSize: "12px",
                          marginTop: "4px",
                        }}
                      >
                        {uploadedFile.error}
                      </div>
                    )}
                  </div>

                  <div className="preview-actions">
                    {uploadedFile.status !== "error" && (
                      <ActionButton
                        type="detail"
                        onClick={() => handlePreview(uploadedFile)}
                        noLabel
                        itemName={uploadedFile.name}
                      />
                    )}
                    <ActionButton
                      type="download"
                      noLabel
                      onClick={() => handleDownload(uploadedFile)}
                    />
                    <ActionButton
                      type="delete"
                      onClick={() => handleRemove(uploadedFile)}
                      disabled={
                        uploadedFile.status === "uploading" || !canDelete
                      }
                      noLabel
                      itemName={uploadedFile.name}
                    />
                  </div>
                </div>

                {uploadedFile.status === "uploading" && (
                  <div className="progress-container">
                    <Progress
                      percent={uploadedFile.progress}
                      status={
                        uploadedFile.progress === 100 ? "success" : "active"
                      }
                      showInfo={true}
                      format={(percent) => `${percent}%`}
                    />
                  </div>
                )}
              </div>
            ))}

            {hasMoreFiles && (
              <div style={{ marginTop: "12px", textAlign: "center" }}>
                <ActionButton
                  type="showMore"
                  onClick={() => setShowAllFiles(!showAllFiles)}
                  showMoreProps={{
                    state: showAllFiles,
                  }}
                />
              </div>
            )}
          </div>
        ) : (
          <Empty
            description={messages["common.noPaymentDocumentsFound"] as string}
          />
        )}
      </div>

      <FilePreviewModal
        visible={previewModalVisible}
        file={previewFile}
        onClose={handleClosePreview}
      />
    </UploadContainer>
  );
}
